import React, { useState, useEffect } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import AdExtensionModal from "./AdExtensionModal";
import KeywordEditModal from "./KeywordEditModal";
import QuantityIncreaseModal from "./QuantityIncreaseModal";
import TabNavigation from "../../../../../components/common/tabNavigation";
import RankCheckModal from "./RankCheckModal";
import {
  rejectAdAPI,
  getMemberAdAPI,
  getMissionHistoryAPI,
  getKeywordNameAPI,
  patchAdExtensionAPI,
} from "../../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../../utils/helpers";
import { useAuth } from "../../../../../hooks/useAuth";
import "../AdDetailModal.css";
import {
  INSTA_PROMOTION_MA_ID,
  guideTextFromMemo,
  parseRequiredWords,
  requiredUrlFromMemo,
} from "../../addAdvertisement/pages/InstaPromotionPage";

const NormalAdDetail = ({
  isOpen,
  onClose,
  adData,
  keywordData, // MyAdvertisement에서 전달받은 키워드 데이터
  priceGradeData, // MyAdvertisement에서 전달받은 가격 등급 데이터
  getUserDetailAPI, // MyAdvertisement에서 전달받은 사용자 상세 정보 API 함수
  onSave, // 키워드 수정 완료 시 호출될 콜백
}) => {
  const { user } = useAuth();
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [isKeywordEditModalOpen, setIsKeywordEditModalOpen] = useState(false);
  const [isQuantityIncreaseModalOpen, setIsQuantityIncreaseModalOpen] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // 탭 상태
  const [isRankCheckModalOpen, setIsRankCheckModalOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]); // 내역 데이터
  const [historyLoading, setHistoryLoading] = useState(false); // 내역 로딩
  const [expandedHistoryIds, setExpandedHistoryIds] = useState(new Set()); // 펼쳐진 내역 ID
  const [keywordMap, setKeywordMap] = useState({}); // 키워드 ID -> 이름 맵

  // 1회 단가 계산 - 결제금액 ÷ 신청수량 (기존 광고의 실제 단가)
  const getUnitPrice = (adData) => {
    const totalPrice = adData.price || 0;
    const totalQuantity = adData.totalQuantity || 0;
    return totalQuantity > 0 ? Math.round(totalPrice / totalQuantity) : 0;
  };

  const unitPrices = {
    [adData.ad_id || adData.id]: getUnitPrice(adData),
  };

  // username은 adData에서 가져오기 (광고 소유자 정보)
  const username = adData.username || adData.member_id || "";

  // 가격 등급 데이터는 props로 받은 것 사용
  const userData = {
    price_grade_detail: {
      data: priceGradeData || [],
    },
  };

  // 내역 데이터 로드 (내역 탭이 활성화될 때)
  useEffect(() => {
    if (activeTab === "history" && isOpen && adData?.ad_id) {
      loadHistoryData();
    }
  }, [activeTab, isOpen, adData?.ad_id]);

  // 내역 데이터 로드 함수
  const loadHistoryData = async () => {
    if (!adData?.ad_id) return;

    try {
      setHistoryLoading(true);
      const response = await getMissionHistoryAPI(adData.ad_id);
      const historyList = response.data?.data || [];
      setHistoryData(historyList);

      // 모든 키워드 ID 수집
      const keywordIds = new Set();
      historyList.forEach((history) => {
        if (history.beforeSnapshot) {
          if (history.beforeSnapshot.mainKeywordId) {
            keywordIds.add(history.beforeSnapshot.mainKeywordId);
          }
          if (history.beforeSnapshot.subKeywordIds) {
            history.beforeSnapshot.subKeywordIds.forEach((id) =>
              keywordIds.add(id)
            );
          }
        }
        if (history.afterSnapshot) {
          if (history.afterSnapshot.mainKeywordId) {
            keywordIds.add(history.afterSnapshot.mainKeywordId);
          }
          if (history.afterSnapshot.subKeywordIds) {
            history.afterSnapshot.subKeywordIds.forEach((id) =>
              keywordIds.add(id)
            );
          }
        }
      });

      // 키워드 이름 가져오기
      const map = {};
      // 현재 로드된 키워드 데이터에서 맵 생성
      if (keywordData && keywordData.length > 0) {
        keywordData.forEach((keyword) => {
          if (keyword.keyword_id) {
            map[keyword.keyword_id] = keyword.name;
          }
        });
      }

      // 아직 맵에 없는 키워드 ID들만 조회
      const missingKeywordIds = Array.from(keywordIds).filter((id) => !map[id]);
      if (missingKeywordIds.length > 0) {
        try {
          const keywordResponse = await getKeywordNameAPI(
            null,
            null,
            missingKeywordIds,
            1,
            2000
          );
          const keywords = keywordResponse.data?.data || [];
          keywords.forEach((keyword) => {
            if (keyword.keyword_id && !map[keyword.keyword_id]) {
              map[keyword.keyword_id] = keyword.name;
            }
          });
        } catch (error) {
          console.error("키워드 이름 로드 실패:", error);
        }
      }

      setKeywordMap(map);
    } catch (error) {
      console.error("내역 데이터 로드 실패:", error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 내역 펼치기/접기 토글
  const toggleHistoryExpanded = (historyId) => {
    setExpandedHistoryIds((prev) => {
      // 시안과 동일: 한 번에 하나만 펼침 (리스트 스크롤로 높이 확보)
      if (prev.has(historyId)) {
        return new Set();
      }
      return new Set([historyId]);
    });
  };

  // 액션 타입 한글 변환
  const getActionLabel = (action) => {
    switch (action) {
      case "STATUS_CHANGED":
        return "상태 변경";
      case "UPDATED":
      case "QUANTITY_CHANGED":
      case "PRICE_CHANGED":
      case "DATE_CHANGED":
        return "업데이트";
      case "CREATED":
        return "생성";
      default:
        return action;
    }
  };

  // 상태 한글 변환
  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  if (!isOpen || !adData) return null;

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "PENDING":
        return "승인대기중";
      case "REJECTED":
        return "거절";
      case "REFUND":
        return "환불";
      case "DONE":
        return "종료";
      default:
        return status;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "status-progress";
      case "PENDING":
        return "status-pending";
      case "PAUSED":
        return "status-paused";
      case "REJECTED":
        return "status-cancelled";
      case "REFUND":
        return "status-refund";
      case "DONE":
        return "status-ended";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].replace(/-/g, ".");
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  const TotalPrice = () => {
    const price = adData.price || 0;
    return formatNumber(price);
  };

  // 서브키워드 배열 생성 (모든 서브키워드 포함)
  const getSubKeywords = () => {
    // keywordData가 배열인 경우 SUB 타입 키워드들을 추출
    const subKeywords = keywordData?.filter((k) => k.type === "SUB") || [];
    return subKeywords.map((keyword) => keyword.name || "-");
  };

  // 광고 취소 핸들러
  const handleCancelAd = async () => {
    if (!adData?.ad_id) {
      alert("광고 정보를 찾을 수 없습니다.");
      return;
    }

    try {
      setIsCancelling(true);

      // 광고 상태 재확인
      const checkResponse = await getMemberAdAPI(
        user?.id || adData.webMemberId || adData.member_id,
        1,
        1,
        { ad_id: adData.ad_id }
      );

      const ads = checkResponse.data?.data || [];
      const currentAd = ads.find((ad) => ad.ad_id === adData.ad_id);

      if (!currentAd) {
        alert("광고를 찾을 수 없습니다.");
        setIsCancelling(false);
        return;
      }

      // status가 "PENDING"이 아니면 취소 불가
      if (currentAd.status !== "PENDING") {
        alert(
          `현재 광고 상태가 "${getStatusText(
            currentAd.status
          )}"이므로 등록 취소할 수 없습니다.`
        );
        setIsCancelling(false);
        // 상태가 변경되었을 수 있으므로 데이터 새로고침
        if (onSave) {
          onSave();
        }
        return;
      }

      const confirmCancel = window.confirm(
        "정말로 이 광고를 취소하시겠습니까?"
      );
      if (!confirmCancel) {
        setIsCancelling(false);
        return;
      }

      // rejectAdAPI 호출 (필요한 파라미터들 전달)
      const currentDeposit = user?.deposit || 0; // 로그인한 사용자의 현재 예치금
      const amount = currentAd.price || adData.price || 0;
      const newDeposit = currentDeposit + amount;

      await rejectAdAPI(
        currentAd.ad_id,
        currentAd.webMemberId || currentAd.member_id,
        currentAd.missionName || currentAd.adName || "광고",
        newDeposit,
        amount,
        "REJECTED",
        currentAd.startDate,
        currentAd.endDate
      );

      alert("광고가 성공적으로 취소되었습니다.");
      onClose(); // 모달 닫기
      // 부모 컴포넌트에서 데이터 새로고침을 위해 onSave 호출
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("광고 취소 실패:", error);
      alert("광고 취소에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsCancelling(false);
    }
  };

  // 광고 종료 핸들러
  const handleEndAd = async () => {
    if (!adData?.ad_id) {
      alert("광고 정보를 찾을 수 없습니다.");
      return;
    }

    // 오늘 날짜 계산 (로컬 시간 기준)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // 내일 날짜 계산
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // 날짜를 YYYY-MM-DD 형식으로 변환하는 함수
    const formatDateToString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const todayString = formatDateToString(today);

    // 광고 종료일
    const endDate = adData.endDate
      ? new Date(adData.endDate.split("T")[0])
      : null;

    if (!endDate) {
      alert("광고 종료일을 찾을 수 없습니다.");
      return;
    }

    // 내일부터 광고 종료일까지의 일수 계산
    const timeDiff = endDate.getTime() - tomorrow.getTime();
    const remainingDays = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24))); // 내일부터 종료일까지 일수

    // dailyQuantity 계산
    let dailyQuantity = adData.dailyQuantity || 0;
    if (
      !dailyQuantity &&
      adData.totalQuantity &&
      adData.startDate &&
      adData.endDate
    ) {
      // totalQuantity와 광고 기간으로 역산
      const startDate = new Date(adData.startDate.split("T")[0]);
      const endDateCalc = new Date(adData.endDate.split("T")[0]);
      const totalDays =
        Math.ceil(
          (endDateCalc.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
        ) + 1;
      if (totalDays > 0) {
        dailyQuantity = Math.round(adData.totalQuantity / totalDays);
      }
    }

    // 1회 단가 계산
    const unitPrice = Math.round(adData.price / adData.totalQuantity) || 0;

    // 환불 금액 계산
    const refundAmount = dailyQuantity * remainingDays * unitPrice;

    // 날짜 포맷팅 함수 (YYYY-MM-DD -> YYYY.MM.DD)
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return "-";
      return dateString.replace(/-/g, ".");
    };

    // 확인 팝업
    const confirmMessage = `광고 마감 날짜를 오늘(${formatDateForDisplay(
      todayString
    )})로 변경 후\n나머지 날짜의 광고 금액은 ${refundAmount.toLocaleString()}원입니다.\n\n광고를 중단하시겠습니까?`;

    const confirmEnd = window.confirm(confirmMessage);
    if (!confirmEnd) return;

    try {
      setIsEnding(true);

      // rejectAdAPI 호출 (status를 "DONE"으로 설정하여 광고 종료)
      const currentDeposit = user?.deposit || 0;
      const newDeposit = currentDeposit + refundAmount; // 환불 금액 추가

      await rejectAdAPI(
        adData.ad_id,
        adData.webMemberId || adData.member_id,
        adData.missionName || adData.adName || "광고",
        newDeposit,
        refundAmount, // 환불 금액
        "DONE",
        adData.startDate,
        todayString // 종료일을 오늘로 변경
      );

      alert("광고가 성공적으로 종료되었습니다.");
      onClose(); // 모달 닫기
      // 부모 컴포넌트에서 데이터 새로고침을 위해 onSave 호출
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("광고 종료 실패:", error);
      alert("광고 종료에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsEnding(false);
    }
  };

  return (
    <>
      <div className="ad-detail-modal-overlay" onClick={onClose}>
        <div className="ad-detail-modal" onClick={(e) => e.stopPropagation()}>
          {/* 헤더 - Flutter 구조 적용 */}
          <div className="modal-header">
            {/* Container */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                width: "100%",
              }}
            >
              {/* Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  flex: 1,
                }}
              >
                {/* 1-1-1. Image */}
                <div className="thumbnail-image">
                  <img
                    src={adData.imageUrl || "/logo192.png"}
                    alt={adData.missionName || "상품 이미지"}
                    className="product-thumbnail"
                    onError={(e) => {
                      e.target.src = "/logo192.png";
                    }}
                  />
                </div>

                {/* 1-1-2. Column */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    flex: 1,
                  }}
                >
                  {/* 1-1-2-1. Store Name */}
                  <div className="store-name">{adData.storeName || "-"}</div>

                  {/* 1-1-2-2. Mission Name */}
                  <div className="mission-name">
                    {adData.missionName || "-"}
                  </div>
                </div>
              </div>

              {/* 1-1-3. Remaining Days */}
              <div className="remaining-days">
                <RemainingDaysBadge
                  startDate={adData.startDate}
                  endDate={adData.endDate}
                  status={adData.status}
                />
              </div>

              {/* 1-1-4. Status */}
              <div className={`status-badge ${getStatusClass(adData.status)}`}>
                {getStatusText(adData.status)}
              </div>

              {/* 1-1-6. 광고연장버튼 (가장 우측에 고정)
              <button
                className="extension-btn"
                disabled={adData.status !== "APPROVED"}
                onClick={() => setIsExtensionModalOpen(true)}
              >
                광고연장1
              </button> */}
            </div>
          </div>

          {/* 탭 네비게이션 */}
          <TabNavigation
            tabs={[
              { id: "details", label: "광고 상세 정보" },
            //   { id: "keywords", label: "키워드 & 순위" },
              ...(user?.role !== "CHECKER" ? [{ id: "history", label: "내역" }] : []),
            ]}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />

          {/* 상세 내용 */}
          <div className="modal-content">
            {activeTab === "details" && (
              <div className="form-grid">
                <div className="form-row">
                  <label>카테고리</label>
                  <div className="form-value">{adData.category || "-"}</div>
                </div>

                <div className="form-row">
                  <label>스토어명</label>
                  <div className="form-value">{adData.storeName || "-"}</div>
                </div>

                <div className="form-row">
                  <label>광고명</label>
                  <div className="form-value">{adData.missionName || "-"}</div>
                </div>

                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>미션 방법</label>
                    <div className="form-value">{adData.productName || "-"}</div>
                  </div>
                )}

                {adData.missionTemplate === INSTA_PROMOTION_MA_ID && (
                  <>
                    <div className="form-row">
                      <label>필수 단어</label>
                      <div className="form-value">
                        {parseRequiredWords(adData.approveAnswer).join(", ") ||
                          "-"}
                      </div>
                    </div>
                    <div className="form-row">
                      <label>포함할 URL</label>
                      <div className="form-value">
                        {requiredUrlFromMemo(adData.memo) || "-"}
                      </div>
                    </div>
                    <div className="form-row">
                      <label>게시 가이드</label>
                      <div className="form-value">
                        {guideTextFromMemo(adData.memo) || "-"}
                      </div>
                    </div>
                  </>
                )}

                <div className="form-row">
                  <label>광고 시작일</label>
                  <div className="form-value">
                    {formatDate(adData.startDate)}
                  </div>
                </div>

                <div className="form-row">
                  <label>광고 종료일</label>
                  <div className="form-value">{formatDate(adData.endDate)}</div>
                </div>

                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>1회 단가</label>
                    <div
                      className="form-value"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={`${formatNumber(
                        Math.round(adData.price / adData.totalQuantity)
                      )}원`}
                    >
                      {formatNumber(
                        Math.round(adData.price / adData.totalQuantity)
                      )}
                      원
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <label>총 수량</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={formatNumber(adData.totalQuantity)}
                  >
                    {formatNumber(adData.totalQuantity)}
                  </div>
                </div>

                <div className="form-row">
                  <label>일일 수량</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={formatNumber(adData.dailyQuantity)}
                  >
                    {formatNumber(adData.dailyQuantity)}
                  </div>
                </div>

                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>결제 금액</label>
                    <div
                      className="form-value"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={`${TotalPrice()}원`}
                    >
                      {TotalPrice()}원
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <label>마감 예정일</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={formatDate(adData.endDate)}
                  >
                    {formatDate(adData.endDate)}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "keywords" && (
              <>
                {/* 키워드 정보 섹션 */}
                <div className="keyword-summary-section">
                  <div className="keyword-section-header">
                    <h4>키워드 정보</h4>
                  </div>
                  <div className="keyword-summary">
                    <div className="keyword-summary-item">
                      <span className="summary-label">메인 키워드:</span>
                      <span className="summary-value main-keyword">
                        {(() => {
                          const mainKeyword = keywordData?.find(
                            (k) => k.type === "MAIN"
                          );
                          return (
                            mainKeyword?.name || adData.mainKeyword || "없음"
                          );
                        })()}
                      </span>
                    </div>
                    <div className="keyword-summary-item">
                      <span className="summary-label">서브 키워드:</span>
                      <div className="sub-keywords-list">
                        {getSubKeywords().length > 0 ? (
                          getSubKeywords().map((keyword, index) => (
                            <span key={index} className="sub-keyword-tag">
                              {keyword}
                            </span>
                          ))
                        ) : (
                          <span className="no-keywords">없음</span>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const mainKeyword = keywordData?.find(
                        (k) => k.type === "MAIN"
                      );
                      const initialRank =
                        mainKeyword?.initialRank ||
                        adData.initialRank ||
                        adData.startRank;
                      const currentRank =
                        mainKeyword?.currentRank ||
                        adData.currentRank ||
                        adData.rank;
                      return (
                        initialRank !== undefined &&
                        currentRank !== undefined && (
                          <div className="keyword-summary-item">
                            <span className="summary-label">순위:</span>
                            <span
                              className="summary-value ad-rank-link"
                              onClick={() => setIsRankCheckModalOpen(true)}
                              style={{ cursor: "pointer", color: "#3b82f6" }}
                            >
                              시작: {initialRank}위 → 현재: {currentRank}위
                            </span>
                          </div>
                        )
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

            {activeTab === "history" && (
              <div className="ad-history-section">
                <h4>변경 내역</h4>
                <div className="ad-history-content">
                  {historyLoading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <p>내역을 불러오는 중...</p>
                    </div>
                  ) : historyData.length === 0 ? (
                    <p
                      style={{
                        color: "#6b7280",
                        textAlign: "center",
                        padding: "40px",
                      }}
                    >
                      변경 내역 데이터가 없습니다.
                    </p>
                  ) : (
                    <div className="ad-history-timeline">
                      {historyData.map((history) => {
                        const isExpanded = expandedHistoryIds.has(history._id);
                        const changedAt = history.changedAt
                          ? convertUTCToKoreaTime(history.changedAt)
                          : null;
                        const changedAtStr = changedAt
                          ? changedAt.toLocaleString("ko-KR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "";

                        return (
                          <div key={history._id} className="ad-history-entry">
                            <div
                              className="ad-history-entry-header"
                              onClick={() => toggleHistoryExpanded(history._id)}
                            >
                              <div className="ad-history-entry-header-left">
                                <span className="ad-history-entry-action">
                                  {getActionLabel(history.action)}
                                </span>
                                <span className="ad-history-entry-reason">
                                  {history.reason || "-"}
                                </span>
                                {history.admin_memo && (
                                  <span className="ad-history-entry-memo">
                                    ({history.admin_memo})
                                  </span>
                                )}
                              </div>
                              <div className="ad-history-entry-header-right">
                                <span className="ad-history-entry-date">
                                  {changedAtStr}
                                </span>
                                <span className="ad-history-entry-toggle">
                                  {isExpanded ? "▼" : "▶"}
                                </span>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="ad-history-entry-details">
                                <div className="ad-history-entry-changed-fields">
                                  <strong>변경된 필드:</strong>{" "}
                                  {history.changedFields?.join(", ") || "-"}
                                </div>
                                <div className="ad-history-entry-snapshots">
                                  <div className="snapshot-section">
                                    <h5 className="snapshot-title before">
                                      변경 전
                                    </h5>
                                    <div className="snapshot-content">
                                      {history.beforeSnapshot ? (
                                        <HistorySnapshot
                                          snapshot={history.beforeSnapshot}
                                          changedFields={
                                            history.changedFields || []
                                          }
                                          keywordMap={keywordMap}
                                        />
                                      ) : (
                                        <p>데이터 없음</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="snapshot-section">
                                    <h5 className="snapshot-title after">
                                      변경 후
                                    </h5>
                                    <div className="snapshot-content">
                                      {history.afterSnapshot ? (
                                        <HistorySnapshot
                                          snapshot={history.afterSnapshot}
                                          changedFields={
                                            history.changedFields || []
                                          }
                                          keywordMap={keywordMap}
                                        />
                                      ) : (
                                        <p>데이터 없음</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            {/* {(adData.status === "APPROVED" || adData.status === "PENDING") && (
              <button
                className="ad-detail-edit-keyword-btn"
                onClick={() => {
                  setIsKeywordEditModalOpen(true);
                }}
              >
                키워드 수정
              </button>
            )}
            {(adData.status === "APPROVED" || adData.status === "PENDING") && (
              <button
                className="ad-detail-increase-quantity-btn"
                onClick={() => {
                  setIsQuantityIncreaseModalOpen(true);
                }}
              >
                수량 증가
              </button>
            )}
            {adData.status === "PENDING" && (
              <button
                className="ad-detail-cancel-btn"
                onClick={handleCancelAd}
                disabled={isCancelling}
              >
                {isCancelling ? "취소 중..." : "등록 취소"}
              </button>
            )}
            {user?.role !== "CHECKER" && adData.status === "APPROVED" && (
              <button
                className="ad-detail-end-btn"
                onClick={handleEndAd}
                disabled={isEnding}
              >
                {isEnding ? "종료 중..." : "광고 종료"}
              </button>
            )} */}
            <button className="ad-detail-close-btn" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>
      </div>

      {/* 광고 연장 모달을 최상위 레벨로 렌더링 */}
      {isExtensionModalOpen && (
        <AdExtensionModal
          isOpen={isExtensionModalOpen}
          onClose={() => setIsExtensionModalOpen(false)}
          selectedAds={[adData]}
          onExtend={(result) => {
            if (result === "success") {
              // 연장 성공 시 모달 닫기
              setIsExtensionModalOpen(false);
              // 부모 컴포넌트에 연장 완료 알림 및 데이터 새로고침
              alert("광고 연장이 성공적으로 완료되었습니다!");
              // 부모 컴포넌트의 데이터 새로고침 함수 호출
              if (onSave) {
                onSave();
              }
            }
          }}
          unitPrices={unitPrices} // unitPrices 데이터 전달
          username={username} // username 데이터 전달
          user={userData} // 가격 등급 데이터가 포함된 userData 전달
          getUserDetailAPI={getUserDetailAPI} // API 함수 전달
        />
      )}

      {/* 키워드 수정 모달 */}
      {isKeywordEditModalOpen && (
        <KeywordEditModal
          isOpen={isKeywordEditModalOpen}
          onClose={() => setIsKeywordEditModalOpen(false)}
          adData={adData}
          keywordData={keywordData}
          onSave={onSave}
        />
      )}

      {/* 수량 증가 모달 */}
      {isQuantityIncreaseModalOpen && (
        <QuantityIncreaseModal
          isOpen={isQuantityIncreaseModalOpen}
          onClose={() => setIsQuantityIncreaseModalOpen(false)}
          adData={adData}
          unitPrice={getUnitPrice(adData)}
          onSave={onSave}
        />
      )}

      {/* 순위체크 모달 */}
      <RankCheckModal
        isOpen={isRankCheckModalOpen}
        onClose={() => setIsRankCheckModalOpen(false)}
        adData={adData}
      />
    </>
  );
};

// 내역 스냅샷 컴포넌트
const HistorySnapshot = ({ snapshot, changedFields = [], keywordMap = {} }) => {
  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  // 필드가 변경되었는지 확인
  const isChanged = (fieldName) => {
    return changedFields.includes(fieldName);
  };

  // 값 포맷팅 함수
  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "예" : "아니오";
    if (Array.isArray(value)) return value.join(", ");
    return value;
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("ko-KR");
    } catch (error) {
      return dateString;
    }
  };

  let memoData = {};
  try {
    if (snapshot.memo) {
      memoData = JSON.parse(snapshot.memo);
    }
  } catch (error) {
    console.error("memo 파싱 실패:", error);
  }

  // 모든 필드를 표시하는 함수
  const renderField = (
    label,
    fieldName,
    value,
    formatter = null,
    forceChanged = false
  ) => {
    const formattedValue = formatter ? formatter(value) : formatValue(value);
    const changed = forceChanged || isChanged(fieldName);

    return (
      <div className="snapshot-row" key={fieldName + label}>
        <span className="snapshot-label">{label}:</span>
        <span className={`snapshot-value ${changed ? "changed-field" : ""}`}>
          {formattedValue}
        </span>
      </div>
    );
  };

  return (
    <div className="snapshot-data">
      {renderField("서비스명", "serviceName", snapshot.serviceName)}
      {renderField("광고명", "missionName", snapshot.missionName)}
      {renderField("미션 브라우저", "openWebSite", snapshot.openWebSite)}
      {renderField("웹 회원 ID", "webMemberId", snapshot.webMemberId)}
      {renderField("상품명", "productName", snapshot.productName)}
      {renderField("스토어명", "storeName", snapshot.storeName)}
      {renderField("상품 URL", "productUrl", snapshot.productUrl)}
      {renderField("상품 ID", "productId", snapshot.productId)}
      {renderField("원부 ID", "originId", snapshot.originId)}
      {renderField("이미지 URL", "imageUrl", snapshot.imageUrl)}
      {renderField("상태", "status", getStatusLabel(snapshot.status))}
      {renderField(
        "가격",
        "price",
        snapshot.price ? `${snapshot.price.toLocaleString()}원` : "-"
      )}
      {renderField("총 수량", "totalQuantity", snapshot.totalQuantity)}
      {renderField("일일 수량", "dailyQuantity", snapshot.dailyQuantity)}
      {renderField("시작일", "startDate", formatDate(snapshot.startDate))}
      {renderField("종료일", "endDate", formatDate(snapshot.endDate))}
      {renderField(
        "메인 키워드",
        "mainKeywordId",
        snapshot.mainKeywordId
          ? `${keywordMap[snapshot.mainKeywordId] || "알 수 없음"} (ID: ${
              snapshot.mainKeywordId
            })`
          : "-"
      )}
      {renderField(
        "서브 키워드",
        "subKeywordIds",
        snapshot.subKeywordIds && snapshot.subKeywordIds.length > 0
          ? snapshot.subKeywordIds
              .map((id) => `${keywordMap[id] || "알 수 없음"} (ID: ${id})`)
              .join(", ")
          : "-"
      )}
      {renderField("미션 분할", "divisionCount", snapshot.divisionCount)}
      {renderField("생성일", "createdAt", formatDate(snapshot.createdAt))}
      {renderField("수정일", "updatedAt", formatDate(snapshot.updatedAt))}
      {/* Memo 필드들 */}
      {memoData.originProductName !== undefined &&
        renderField(
          "원본 상품명",
          "memo",
          memoData.originProductName,
          null,
          isChanged("memo")
        )}
      {memoData.originPrice !== undefined &&
        renderField(
          "원가",
          "memo",
          memoData.originPrice,
          null,
          isChanged("memo")
        )}
      {memoData.unitPrice !== undefined &&
        renderField(
          "적용단가",
          "memo",
          memoData.unitPrice,
          null,
          isChanged("memo")
        )}
      {memoData.adTag !== undefined &&
        renderField(
          "광고 태그",
          "memo",
          memoData.adTag,
          null,
          isChanged("memo")
        )}
    </div>
  );
};

export default NormalAdDetail;
